import { yupResolver } from '@hookform/resolvers';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  TextField,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import TuneIcon from '@material-ui/icons/Tune';
import { Autocomplete } from '@material-ui/lab';
import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import InputCheckbox from '../../../../../../components/Inputs/Checkbox';
import DialogTitleBlock from '../../../../../../components/Modals/Dialog/DialogTitleBlock';
import { useAuth } from '../../../../../../contexts/AuthContext/AuthContext';
import { benchwiseValidate } from '../../../../../../core/api/benchmark';
import { ResolveBenchmarkCompanyRequest } from '../../../../../../generated/model/resolveBenchmarkCompanyRequest';
import { useCompaniesState } from '../../../../../FinancialData/shared/CompanyContext/CompanyContext';
import { useErrorsDispatch } from '../../../../../FinancialData/shared/ErrorContext/ErrorContext';
import { ErrorContextActionTypes } from '../../../../../FinancialData/shared/ErrorContext/ErrorContext.types';
import { useBenchmarkState } from '../../../../Context/BenchmarkContext';

export type ValidationTypeEnum = 'BENCHWISE_FAST' | 'VERIFYWISE';
export const ValidationTypeEnum = {
  BENCWISE_FAST: 'BENCHWISE_FAST' as ValidationTypeEnum,
  VERIFYWISE: 'VERIFYWISE' as ValidationTypeEnum,
};

interface OwnProps {
  show: boolean;
  title: string;
  comment: string | null | undefined;
  validationType: ValidationTypeEnum;
  handleSave: () => void;
  handleClose: () => void;
}

interface ValidationForm {
  validationComment: string;
  applyToAll: boolean;
  analyzeWebsite: boolean;
}

const BenchwiseValidateModal: React.FC<OwnProps> = ({
  show,
  title,
  comment,
  validationType,
  handleClose,
  handleSave,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [disabled, setDisabled] = useState(false);
  const [options, setOptions] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const { activeBenchmark } = useBenchmarkState();
  // currently not used, but will add later on
  // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
  const { company } = useCompaniesState();
  const { user } = useAuth();
  const dispatchErrors = useErrorsDispatch();

  const { handleSubmit, errors, control, clearErrors, register } = useForm<ValidationForm>({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    resolver: yupResolver(validationSchema(validationType)),
  });

  useEffect(() => {
    if (!show) {
      return setOptions([]);
    }
    (async () => {
      try {
        setLoading(true);
        // later on we'll fetch the last bechmark study descriptions from the backend
        const comments: string[] = [];
        setOptions(comments);
      } catch (error) {
        dispatchErrors({
          type: ErrorContextActionTypes.SetError,
          payload: { error },
        });
      } finally {
        setLoading(false);
      }
    })();
  }, [show, validationType, activeBenchmark, dispatchErrors]);

  const onSubmit = async (formData: ValidationForm) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
    const req: ResolveBenchmarkCompanyRequest = {
      status: undefined,
      comment: formData.validationComment,
      applyForRemaining: false,
    };
    setDisabled(true);
    try {
      if (
        await benchwiseValidate(
          activeBenchmark?.id!,
          formData.validationComment,
          formData.applyToAll,
          formData.analyzeWebsite,
        )
      ) {
        handleSave(); // Only call handleSave, let parent handle reload
      }
    } catch (error) {
      dispatchErrors({
        type: ErrorContextActionTypes.SetError,
        payload: { error },
      });
    } finally {
      setDisabled(false);
    }
  };

  return (
    <Box>
      <Dialog
        disableRestoreFocus={true}
        onClose={() => {
          handleClose();
        }}
        open={show}
        className={classes.dialogBox}
      >
        <DialogTitleBlock
          icon={<TuneIcon />}
          title={title}
          handleClose={handleClose}
          disabled={disabled}
        />
        <DialogContent>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Controller
              render={({ onChange, ...props }) => (
                <Autocomplete
                  freeSolo
                  loading={loading}
                  options={options}
                  autoSelect
                  renderInput={(params) => {
                    params.InputProps = {
                      ...params.InputProps,
                      endAdornment: (
                        <React.Fragment>
                          {loading ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </React.Fragment>
                      ),
                      className: '', // className in InputProps is MuiAutocomplete-inputRoot. It renders too big input box
                    };
                    return (
                      <>
                        <TextField
                          {...params}
                          label={t('benchmark:resolveBenchmarkCompany.benchwiseValidateField')}
                          variant="outlined"
                          margin="normal"
                          error={!!errors.validationComment}
                          autoFocus
                        />
                        <Box style={{ color: 'red' }}>
                          <Typography variant="caption">
                            {t(errors.validationComment?.message || '')}
                          </Typography>
                        </Box>
                      </>
                    );
                  }}
                  onChange={(e: React.ChangeEvent<{}>, value: string | null) => onChange(value)}
                  onOpen={() => clearErrors('validationComment')}
                  {...props}
                />
              )}
              onChange={(data: string) => data}
              defaultValue={comment}
              name="validationComment"
              control={control}
            />
            <Box display="flex" alignItems="center" mt={2}>
              <Box display="flex" flexDirection="column" mt={2}>
                <InputCheckbox
                  label={t('benchmark:resolveBenchmarkCompany.benchwiseAnalyzeWebsite')}
                  name="analyzeWebsite"
                  defaultValue={false}
                  refReg={register}
                  disabled={
                    !!(window as any)?.env?.DISABLE_VERIFYWISE && !user?.showPreviewFeatures
                  }
                />
                <InputCheckbox
                  label={t('benchmark:resolveBenchmarkCompany.benchwiseApplyForAll')}
                  name="applyToAll"
                  defaultValue={false}
                  refReg={register}
                />
              </Box>
              <Button
                type="submit"
                color="primary"
                variant="contained"
                disabled={disabled}
                className={classes.button}
              >
                {t('app:ok')}
              </Button>
            </Box>
          </form>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
const validationSchema = (validationType: ValidationTypeEnum) => {
  const validationFields = {
    validationComment: yup.string().nullable().required('companies:requiredField'),
  };
  return yup.object().shape(validationFields);
};

const useStyles = makeStyles((theme) => ({
  dialogBox: {
    '& .MuiDialogContent-root .MuiPaper-root': {
      borderRadius: 0,
    },
    '& .MuiPaper-root': {
      minWidth: '500px',
    },
  },
  button: {
    marginRight: theme.spacing(5),
  },
}));

export default BenchwiseValidateModal;
